/**
 * La ficha de un integrante del equipo, vista por su jefatura (o suplente), en un
 * panel lateral sobre «Mi equipo»: la lista queda a la vista y se pasa de una
 * persona a otra sin perder el lugar.
 *
 * Muestra solo lo que una jefatura necesita para dirigir: datos laborales y
 * contractuales, contacto (también para emergencias), cumpleaños sin año y
 * ausencias. Lo personal y lo previsional (RUT, dirección, AFP, salud, cuenta,
 * sueldo) ni siquiera llega del servidor (Ley 21.719, minimización).
 */
import { Mail, Phone } from 'lucide-react'
import { Drawer } from '@/components/ds/Drawer'
import { EmptyState } from '@/components/ds/EmptyState'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { VACATION_REQUEST_STATUS, LEAVE_REQUEST_STATUS } from '@/components/ds/statusCatalogs'
import { usePortalTeamMemberFicha, type PortalFichaEquipo } from '../hooks/useEmployeePortal'
import { Dato, EncabezadoFicha } from './FichaResumida'
import { antiguedadDesde, dias, fechaCorta, o } from './formatos'

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre']

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section aria-label={titulo} className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">{titulo}</h3>
      <dl className="flex flex-col gap-3">{children}</dl>
    </section>
  )
}

function Contenido({ f }: { f: PortalFichaEquipo }) {
  const t = f.trabajo
  const areaDivision = [t.area, t.division].filter(Boolean).join(' · ')
  return (
    <div className="flex flex-col gap-6">
      <EncabezadoFicha ficha={f} />

      <Bloque titulo="Contacto">
        <Dato rotulo="Correo">
          {f.contacto.correo
            ? <a className="inline-flex items-center gap-1.5 text-primary hover:underline" href={`mailto:${f.contacto.correo}`}>
                <Mail size={14} aria-hidden="true" />{f.contacto.correo}
              </a>
            : '—'}
        </Dato>
        <Dato rotulo="Teléfono">
          {f.contacto.telefono
            ? <a className="inline-flex items-center gap-1.5 text-primary hover:underline" href={`tel:${f.contacto.telefono}`}>
                <Phone size={14} aria-hidden="true" />{f.contacto.telefono}
              </a>
            : '—'}
        </Dato>
        <Dato rotulo="Cumpleaños">
          {f.cumpleanos ? `${f.cumpleanos.dia} de ${MESES[f.cumpleanos.mes - 1]}` : '—'}
        </Dato>
      </Bloque>

      <Bloque titulo="Trabajo y contrato">
        <Dato rotulo="Área · División">{o(areaDivision)}</Dato>
        <Dato rotulo="Centro de costo">{o(t.centro_de_costo)}</Dato>
        <Dato rotulo="Contrato">
          {t.contrato
            ? `${t.contrato}${t.contrato_termino ? `, hasta el ${fechaCorta(t.contrato_termino)}` : ''}`
            : '—'}
        </Dato>
        <Dato rotulo="Jornada">{t.jornada_semanal ? `${Number(t.jornada_semanal)} horas semanales` : '—'}</Dato>
        <Dato rotulo="Ingreso">{t.ingreso ? `${fechaCorta(t.ingreso)} (${antiguedadDesde(t.ingreso)})` : '—'}</Dato>
        <Dato rotulo="Jefatura directa">{o(t.jefatura)}</Dato>
      </Bloque>

      <Bloque titulo="Vacaciones">
        <Dato rotulo="Días disponibles">{dias(f.vacaciones.disponibles)} días</Dato>
        <Dato rotulo="Legales · Progresivas">
          {dias(f.vacaciones.legales)} · {dias(f.vacaciones.progresivas)}
        </Dato>
        <Dato rotulo="Próximas vacaciones">
          {f.vacaciones.proxima
            ? `Del ${fechaCorta(f.vacaciones.proxima.desde)} al ${fechaCorta(f.vacaciones.proxima.hasta)}`
            : 'Sin vacaciones aprobadas por delante'}
        </Dato>
      </Bloque>

      <section aria-label="Ausencias recientes" className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">Ausencias recientes</h3>
        {f.ausencias.length === 0 ? (
          <p className="text-sm text-content-muted">Sin vacaciones ni permisos registrados.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-divider">
            {f.ausencias.map((a, i) => (
              <li key={`${a.tipo}-${a.desde}-${i}`} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-content">{a.detalle}</p>
                  <p className="text-xs text-content-muted tabular-nums">
                    {a.desde === a.hasta ? fechaCorta(a.desde) : `${fechaCorta(a.desde)} – ${fechaCorta(a.hasta)}`}
                    {' · '}{dias(a.dias)} {Number(a.dias) === 1 ? 'día' : 'días'}
                  </p>
                </div>
                <StatusBadge status={a.estado}
                  catalog={a.tipo === 'vacaciones' ? VACATION_REQUEST_STATUS : LEAVE_REQUEST_STATUS} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-xs text-content-subtle">
        Los datos personales, previsionales y de remuneraciones no se muestran a la jefatura.
      </p>
    </div>
  )
}

export function FichaDelIntegrante({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, isError } = usePortalTeamMemberFicha(id)
  return (
    <Drawer open={!!id} onClose={onClose} side="right" size={480} title="Ficha del integrante">
      {isLoading && <p className="text-sm text-content-muted">Cargando…</p>}
      {isError && <EmptyState variant="error" title="No se pudo cargar la ficha" />}
      {data && <Contenido f={data} />}
    </Drawer>
  )
}
